import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';

interface Item {
    id: number;
    category: string;
    sub_category: string;
    title: string;
    author: string;
    status: string;
    source: 'items' | 'items1' | 'items2';
}

interface CurrentDraftsTableProps {
    items: Item[];
    onView: (id: number) => void;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    onView1: (id: number) => void;
    onEdit1: (id: number) => void;
    onDelete1: (id: number) => void;
    onView2: (id: number) => void;
    onEdit2: (id: number) => void;
    onDelete2: (id: number) => void;
    author?: string | undefined | null;
}

export default function CurrentDashboardDraftsTable({
    items,
    onView,
    onEdit,
    onDelete,
    onView1,
    onEdit1,
    onDelete1,
    onView2,
    onEdit2,
    onDelete2,
    author
}: CurrentDraftsTableProps) {
    const [latestPage, setLatestPage] = useState(1);
    const latestPerPage = 10;
    const filteredItems = items.filter(
        (item) => item.status === 'Pending' && (!author || item.author === author)
    );
    const totalPages = Math.ceil(filteredItems.length / latestPerPage);
    const indexOfLastLatest = latestPage * latestPerPage;
    const indexOfFirstLatest = indexOfLastLatest - latestPerPage;
    const currentLatest = filteredItems.slice(
        indexOfFirstLatest, 
        indexOfLastLatest
    );

    const nextLatestPage = () => {
        if (latestPage < totalPages) {
            setLatestPage(latestPage + 1);
        }
    };

    const prevLatestPage = () => {
        if (latestPage > 1) {
            setLatestPage(latestPage - 1);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [latestPage]);

    const handleView = (item: Item) => {
        if (item.source === 'items') onView(item.id);
        else if (item.source === 'items1') onView1(item.id);
        else onView2(item.id);
    };

    const handleEdit = (item: Item) => {
        if (item.source === 'items') onEdit(item.id);
        else if (item.source === 'items1') onEdit1(item.id);
        else onEdit2(item.id);
    };

    const handleDelete = (item: Item) => {
        if (item.source === 'items') onDelete(item.id);
        else if (item.source === 'items1') onDelete1(item.id);
        else onDelete2(item.id);
    };

    return (
        <>
            <table className="w-full">
                <thead className="bg-gray-200">
                    <tr>
                        <th style={{ width: '10%' }}>Category</th>
                        <th style={{ width: '15%' }}>Sub Category</th>
                        <th style={{ width: '25%' }}>Title</th>
                        <th style={{ width: '20%' }}>Author</th>
                        <th style={{ width: '10%' }}>Status</th>
                        <th style={{ width: '10%' }} className="text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {currentLatest.length > 0 ? (
                        currentLatest.map(item => (
                            <tr key={item.id}>
                                <td>{item.category}</td>
                                <td>{item.sub_category}</td>
                                <td>{item.title}</td>
                                <td>{item.author}</td>
                                <td>{item.status}</td>
                                <td className="text-center">
                                    <div className="flex justify-center gap-1">
                                        <button
                                            onClick={() => handleView(item)}
                                            data-tooltip-id="preview">
                                            <FontAwesomeIcon icon={faEye} className="text-white bg-red-600 rounded-sm p-1" />
                                        </button>

                                        <button
                                            onClick={() => handleEdit(item)}
                                            data-tooltip-id="edit">
                                            <FontAwesomeIcon icon={faPenToSquare} className="text-white bg-red-600 rounded-sm p-1" />
                                        </button>
                                        
                                        <button
                                            onClick={() => handleDelete(item)}
                                            data-tooltip-id="delete">
                                            <FontAwesomeIcon icon={faTrash} className="text-white bg-gray-600 rounded-sm p-1" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} className="text-center py-4">
                                No data available!
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center gap-2 items-center mt-2 space-x-2">
                    <button
                        onClick={() => setLatestPage(1)}
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-gray-500"
                        disabled={latestPage === 1}>
                        FIRST
                    </button>

                    <button 
                        onClick={prevLatestPage} 
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-gray-500"
                        disabled={latestPage === 1}>
                        PREVIOUS
                    </button>

                    {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;

                        // Display only the current page, one before, and one after it (3 pages at most)
                        const pageRange = 1; // Number of pages before and after the current page
                        const startPage = Math.max(1, latestPage - pageRange); // Prevent going below 1
                        const endPage = Math.min(totalPages, latestPage + pageRange); // Prevent going above totalPages

                        // Only display 3 pages: current page, one before, one after
                        if (pageNum >= startPage && pageNum <= endPage) {
                            return (
                            <button
                            key={pageNum}
                            onClick={() => setLatestPage(pageNum)}
                            className={`px-3 py-1 Univers-BoldCondensed text-xs rounded ${
                                latestPage === pageNum
                                ? "bg-red-600 text-white"
                                : "bg-gray-200 text-black"
                            }`}>
                            {pageNum}
                            </button>
                            );
                        }

                        return null; 
                    })}

                    <button
                        onClick={nextLatestPage}
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-red-500"
                        disabled={latestPage === Math.ceil(items.length / latestPerPage)}>
                        NEXT
                    </button>

                    <button
                        onClick={() => setLatestPage(totalPages)}
                        className="px-3 py-1 rounded disabled:opacity-50 Univers-BoldCondensed text-white bg-red-500"
                        disabled={latestPage === totalPages}>
                        LAST
                    </button>
                </div>
            )}
        </>
    );
}
